import React from 'react'
import { WIZARD_SCREEN_TYPES } from '../constants'
import IntroView from '../components/IntroView'
import CallToActionView from '../components/CallToActionView'
import Moon from '../../../assets/crescent-moon.png'
import KennelSharing from './KennelSharing'
import Dropoff from './Dropoff'
import Pickup from './Pickup'
import NightProgram from './NightProgram'
import FoodSchedule from './FoodSchedule'
import MedicineSchedule from './MedicineSchedule'
import AddOnSelection from './AddOnSelection'
import CancelationPolicy from './CancelationPolicy'
import DepositNotification from './DepositNotification'
import ServiceSummaryAndPayment from './ServiceSummaryAndPayment'
import { VISIT_TYPES } from '../../../utils'

export const BOARDING_FIELDS = {
	dates: 'dates',
	sharing_kennels: 'sharing_kennels',
	early_dropoff: 'early_dropoff',
	extended_pickup: 'extended_pickup',
	first_day_daycare: 'first_day_daycare',
	last_day_daycare: 'last_day_daycare',
	pets: 'pets',
	add_on_services: 'add_on_services',
	medications: 'medications',
	copy_food_addons_to_all_pets: 'copy_food_addons_to_all_pets',
	pet_food_schedules: 'pet_food_schedules',
	copy_kennel_options_to_all_pets: 'copy_kennel_options_to_all_pets',
	copy_kennel_options_from_pet_id: 'copy_kennel_options_from_pet_id',
	copy_other_addons_to_all_pets: 'copy_other_addons_to_all_pets',
	not_present_meds: 'not_present_meds',
	cancelation_terms_accepted: 'cancelation_terms_accepted',
	boarding_options: 'boarding_options',
	deposit_paid: 'deposit_paid',
	status: 'status',
	deposit_terms_accepted: 'deposit_terms_accepted',
	deposit_required: 'deposit_required',
	applied_promo: 'applied_promo',
	household_id: 'household_id',
	type: 'type'
}

export const DEFAULT_DETAILS = {
	_id: '',
	[BOARDING_FIELDS.dates]: [],
	[BOARDING_FIELDS.sharing_kennels]: false,
	[BOARDING_FIELDS.early_dropoff]: false,
	[BOARDING_FIELDS.extended_pickup]: false,
	[BOARDING_FIELDS.first_day_daycare]: false,
	[BOARDING_FIELDS.last_day_daycare]: false,
	[BOARDING_FIELDS.boarding_options]: [],
	[BOARDING_FIELDS.copy_kennel_options_from_pet_id]: '',
	[BOARDING_FIELDS.pets]: [],
	[BOARDING_FIELDS.add_on_services]: [],
	[BOARDING_FIELDS.medications]: [],
	[BOARDING_FIELDS.copy_food_addons_to_all_pets]: false,
	[BOARDING_FIELDS.pet_food_schedules]: {},
	[BOARDING_FIELDS.copy_other_addons_to_all_pets]: false,
	[BOARDING_FIELDS.copy_kennel_options_to_all_pets]: false,
	[BOARDING_FIELDS.not_present_meds]: [],
	[BOARDING_FIELDS.cancelation_terms_accepted]: false,
	[BOARDING_FIELDS.deposit_paid]: false,
	[BOARDING_FIELDS.status]: {},
	[BOARDING_FIELDS.deposit_terms_accepted]: false,
	[BOARDING_FIELDS.deposit_required]: false,
	[BOARDING_FIELDS.applied_promo]: null,
	[BOARDING_FIELDS.type]: VISIT_TYPES.Boarding,
	[BOARDING_FIELDS.household_id]: ''
}

export const SECTION_IDS = {
	section1: 'section1',
	section2: 'section2',
	section3: 'section3'
}
export const SECTIONS = {
	[SECTION_IDS.section1]: {
		sectionName: 'Section One',
		sectionTitle: 'Booking Preferences',
		sectionDescription:
			"Let's begin by customizing the boarding appointment and setting kennel preferences, daycare needs and additional enrichments."
	},
	[SECTION_IDS.section2]: {
		sectionName: 'Section Two',
		sectionTitle: 'Terms & Conditions',
		sectionDescription:
			"Next, let's go over our terms and conditions with the client to make sure they understand our cancellation policy and deposit policy, if one is required."
	},
	[SECTION_IDS.section3]: {
		sectionName: 'Section Three',
		sectionTitle: 'Confirm & Book',
		sectionDescription:
			'Review the boarding services requested, confirm payment information, and finalize the appointment.'
	}
}

export const WORKFLOW = [
	{
		type: WIZARD_SCREEN_TYPES.Start,
		component: (props = {}) => (
			<CallToActionView
				mainText={`Hi ${props?.userName}, let's book a boarding appointment!`}
				secondaryText={
					props?.petNames
						? `Click 'Get Started' to begin scheduling a boarding appointment for ${props?.petNames}.`
						: ''
				}
				imageSrc={Moon}
				actionTitle="Get Started"
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.section1]} {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.sharing_kennels,
		requires_api_update: true,
		display_step_condition: (state) => state?.pets?.length > 1,
		can_proceed_condition: (state) => typeof state?.sharing_kennels === 'boolean',
		component: (props = {}) => <KennelSharing title="Do you want your dogs to share a kennel?*" {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.first_day_daycare,
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.first_day_daycare === 'boolean',
		component: (props = {}) => <Dropoff title="Do you need daycare on your drop-off day?*" {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.last_day_daycare,
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.last_day_daycare === 'boolean',
		component: (props = {}) => <Pickup title="Do you need daycare on your pick-up day?*" {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.boarding_options,
		requires_multiple_views: (state) => {
			return state?.pets.length > 1 && !state?.copy_kennel_options_to_all_pets && !state.sharing_kennels
		},
		create_multiple_views_from: 'pets',
		individual_props_for_each_view_component: ['_id', 'name'],
		requires_api_update: true,
		allow_copy_to_all: true,
		copy_to_all_attr: 'copy_kennel_options_to_all_pets',
		can_proceed_condition: () => true,
		component: (props = {}) => <NightProgram {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.pet_food_schedules,
		requires_multiple_views: (state) => {
			return state?.pets.length > 1 && !state?.copy_food_addons_to_all_pets
		},
		create_multiple_views_from: 'pets',
		individual_props_for_each_view_component: ['_id', 'name'],
		requires_api_update: true,
		allow_copy_to_all: true,
		copy_to_all_attr: 'copy_food_addons_to_all_pets',
		can_proceed_condition: (state, step) => {
			const schedules = state?.[BOARDING_FIELDS.pet_food_schedules] || {}
			const petId = step?._id || state?.pets?.[0]?._id
			const schedule = schedules[petId] || []
			return Array.isArray(schedule) && schedule.length > 0
		},
		component: (props = {}) => <FoodSchedule {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.not_present_meds,
		requires_multiple_views: (state) => state?.pets?.length > 1,
		display_step_condition: (state) => state?.medications?.length > 0,
		specific_view_in_multiple_views_not_required_when: ['medication', (medication) => medication.length === 0],
		create_multiple_views_from: 'pets',
		individual_props_for_each_view_component: ['_id', 'name'],
		requires_api_update: true,
		can_proceed_condition: () => true,
		component: (props = {}) => <MedicineSchedule {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'add_on_services',
		requires_multiple_views: (state) => state?.pets.length > 1 && !state?.copy_other_addons_to_all_pets,
		create_multiple_views_from: 'pets',
		individual_props_for_each_view_component: ['_id', 'name'],
		requires_api_update: true,
		copy_to_all_attr: 'copy_other_addons_to_all_pets',
		can_proceed_condition: (state) => true,
		component: (props = {}) => <AddOnSelection {...props} />
	},
	{
		section: SECTION_IDS.section2,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.section2]} {...props} />
	},
	{
		section: SECTION_IDS.section2,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.cancelation_terms_accepted,
		requires_api_update: false,
		can_proceed_condition: (state) => state?.cancelation_terms_accepted === true,
		component: (props = {}) => (
			<CancelationPolicy
				title="Do you agree to our cancellation policy? *"
				subtext="A copy of our cancellation policy will be provided on the client's app under their appointment's details page."
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.section2,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: BOARDING_FIELDS.deposit_terms_accepted,
		requires_api_update: false,
		can_proceed_condition: (state) => state?.deposit_terms_accepted === true,
		display_step_condition: (state) => state?.deposit_required === true,
		component: (props = {}) => (
			<DepositNotification
				title="Do you agree to our holiday deposit policy? *"
				subtext="This appointment requires a holiday deposit, which will be included in the invoice before you proceed with checkout. Please ensure the client acknowledges this requirement before moving forward."
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.section3]} {...props} />
	},
	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Invoice,
		component: (props = {}) => <ServiceSummaryAndPayment {...props} />
	},
	{
		type: WIZARD_SCREEN_TYPES.Exit,
		component: (props = {}) => (
			<CallToActionView
				mainText={`You did it! You booked boarding for ${props?.petNames}.`}
				secondaryText={`Exit the wizard to view the scheduled appointment for ${props?.petNames}. `}
				imageSrc={Moon}
				actionTitle="Exit"
				{...props}
			/>
		)
	}
]
